/**
 * GoogleAnalyticsManager - Sistema de control condicional para Google Analytics
 * Maneja la carga y configuración de GA basado en consentimiento de cookies y entorno
 */

class GoogleAnalyticsManager {
  constructor() {
    this.gaId = 'UA-59807770-1';
    this.isInitialized = false;
    this.isEnabled = false;
    this.environment = this.getEnvironment();
    
    // Configuración por entornos
    this.environmentConfig = {
      development: {
        enabled: false, // Deshabilitado por defecto en desarrollo
        debug: true,
        testMode: true
      },
      staging: {
        enabled: true,
        debug: true,
        testMode: true
      },
      production: {
        enabled: true,
        debug: false,
        testMode: false
      }
    };
  }

  /**
   * Detecta el entorno actual
   */
  getEnvironment() {
    const hostname = window.location.hostname;
    
    if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.includes('192.168')) {
      return 'development';
    }
    
    if (hostname.includes('staging') || hostname.includes('test')) {
      return 'staging';
    }
    
    return 'production';
  }

  /**
   * Obtiene la configuración del entorno actual
   */
  getConfig() {
    return this.environmentConfig[this.environment] || this.environmentConfig.production;
  }

  /**
   * Verifica si el usuario ha dado consentimiento para cookies analíticas
   */
  hasAnalyticsConsent() {
    try {
      const cookieConsent = localStorage.getItem('cookieConsent');
      if (!cookieConsent) return false;
      
      const consent = JSON.parse(cookieConsent);
      
      // Compatibilidad con el nuevo formato (v2.0) y el formato anterior
      if (consent.version === '2.0' && consent.settings) {
        return consent.settings.analytics === true;
      }
      
      // Formato anterior (retrocompatibilidad)
      return consent.analytics === true;
    } catch (error) {
      console.warn('Error al verificar consentimiento de cookies:', error);
      return false;
    }
  }

  /**
   * Verifica si GA debe estar habilitado según todas las condiciones
   */
  shouldEnableGA() {
    const config = this.getConfig();
    const hasConsent = this.hasAnalyticsConsent();
    const environmentEnabled = config.enabled;
    
    if (config.debug) {
      console.log('GoogleAnalytics - Verificación de condiciones:', {
        environment: this.environment,
        environmentEnabled,
        hasConsent,
        shouldEnable: environmentEnabled && hasConsent
      });
    }
    
    return environmentEnabled && hasConsent;
  }

  /**
   * Carga dinámicamente el script de Google Analytics
   */
  loadGAScript() {
    return new Promise((resolve, reject) => {
      // Verificar si ya existe el script
      if (document.querySelector(`script[src*="googletagmanager.com/gtag/js"]`)) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${this.gaId}`;
      
      script.onload = () => {
        console.log('Google Analytics script cargado exitosamente');
        resolve();
      };
      
      script.onerror = () => {
        console.error('Error al cargar Google Analytics script');
        reject(new Error('Failed to load GA script'));
      };
      
      document.head.appendChild(script);
    });
  }

  /**
   * Inicializa Google Analytics
   */
  async initializeGA() {
    if (this.isInitialized) {
      console.warn('Google Analytics ya está inicializado');
      return;
    }

    try {
      // Cargar script de GA
      await this.loadGAScript();
      
      // Inicializar dataLayer y gtag
      window.dataLayer = window.dataLayer || [];
      window.gtag = function() {
        window.dataLayer.push(arguments);
      };
      
      // Configurar GA
      window.gtag('js', new Date());
      
      const config = this.getConfig();
      const gtagConfig = {
        // Configuración básica
        send_page_view: true,
        
        // Configuración de privacidad
        anonymize_ip: true,
        
        // Configuración de entorno
        debug_mode: config.debug,
        
        // Configuración de cookies
        cookie_flags: 'SameSite=Strict;Secure'
      };
      
      // En modo test, deshabilitar envío real
      if (config.testMode) {
        gtagConfig.send_page_view = false;
        console.log('Google Analytics en modo test - no se enviarán datos reales');
      }
      
      window.gtag('config', this.gaId, gtagConfig);
      
      this.isInitialized = true;
      this.isEnabled = true;
      
      console.log(`Google Analytics inicializado en entorno: ${this.environment}`);
      
    } catch (error) {
      console.error('Error al inicializar Google Analytics:', error);
      throw error;
    }
  }

  /**
   * Deshabilita Google Analytics y limpia cookies
   */
  disableGA() {
    if (!this.isEnabled) return;
    
    // Deshabilitar GA
    if (window.gtag) {
      window.gtag('config', this.gaId, {
        send_page_view: false,
        transport_type: 'beacon'
      });
    }
    
    // Limpiar cookies de GA
    this.clearGACookies();
    
    this.isEnabled = false;
    console.log('Google Analytics deshabilitado y cookies limpiadas');
  }

  /**
   * Limpia todas las cookies de Google Analytics
   */
  clearGACookies() {
    const gaCookies = [
      '_ga',
      '_ga_' + this.gaId.replace('UA-', '').replace('-', '_'),
      '_gat_gtag_' + this.gaId.replace('-', '_'),
      '_gid'
    ];
    
    gaCookies.forEach(cookieName => {
      // Limpiar para el dominio actual
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
      
      // Limpiar para subdominios
      const domain = window.location.hostname;
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${domain};`;
    });
    
    console.log('Cookies de Google Analytics limpiadas');
  }

  /**
   * Envía un evento personalizado a GA (solo si está habilitado)
   */
  trackEvent(eventName, parameters = {}) {
    if (!this.isEnabled || !window.gtag) {
      console.log('GA no habilitado - evento no enviado:', eventName, parameters);
      return;
    }
    
    const config = this.getConfig();
    if (config.testMode) {
      console.log('GA en modo test - evento simulado:', eventName, parameters);
      return;
    }
    
    window.gtag('event', eventName, parameters);
    console.log('Evento enviado a GA:', eventName, parameters);
  }

  /**
   * Envía una página vista a GA (solo si está habilitado)
   */
  trackPageView(pagePath) {
    if (!this.isEnabled || !window.gtag) {
      console.log('GA no habilitado - página vista no enviada:', pagePath);
      return;
    }
    
    const config = this.getConfig();
    if (config.testMode) {
      console.log('GA en modo test - página vista simulada:', pagePath);
      return;
    }
    
    window.gtag('config', this.gaId, {
      page_path: pagePath
    });
    
    console.log('Página vista enviada a GA:', pagePath);
  }

  /**
   * Actualiza el estado de GA basado en el consentimiento actual
   */
  async updateConsentStatus() {
    const shouldEnable = this.shouldEnableGA();
    
    if (shouldEnable && !this.isEnabled) {
      // Habilitar GA
      await this.initializeGA();
    } else if (!shouldEnable && this.isEnabled) {
      // Deshabilitar GA
      this.disableGA();
    }
  }

  /**
   * Obtiene el estado actual de GA para debugging
   */
  getStatus() {
    return {
      environment: this.environment,
      isInitialized: this.isInitialized,
      isEnabled: this.isEnabled,
      hasConsent: this.hasAnalyticsConsent(),
      config: this.getConfig(),
      gaId: this.gaId
    };
  }

  /**
   * Método para testing - fuerza la habilitación de GA
   */
  forceEnable() {
    console.warn('Forzando habilitación de GA para testing');
    return this.initializeGA();
  }

  /**
   * Método para testing - fuerza la deshabilitación de GA
   */
  forceDisable() {
    console.warn('Forzando deshabilitación de GA para testing');
    this.disableGA();
  }
}

// Crear instancia singleton
const googleAnalyticsManager = new GoogleAnalyticsManager();

// Exponer globalmente para debugging
if (typeof window !== 'undefined') {
  window.GAManager = googleAnalyticsManager;
}

export default googleAnalyticsManager;